import axios from 'axios';
import { supabase } from './supabase';
import { BACKEND_URL } from './config';
import { SUPABASE_URL } from '../auth/constants';

async function getValidAccessToken() {
    try {
        const projectRef = SUPABASE_URL.split('//')[1].split('.')[0];
        const storageKey = `sb-${projectRef}-auth-token`;
        const storedSession = localStorage.getItem(storageKey);
        
        if (storedSession) {
            try {
                const parsed = JSON.parse(storedSession);
                if (parsed?.access_token) {
                    return parsed.access_token;
                }
            } catch (e) {
                // Ignore parse errors
            }
        }
        
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('getSession timeout')), 5000)
        );
        
        try {
            const { data: { session }, error: sessionError } = await Promise.race([
                sessionPromise,
                timeoutPromise
            ]);
            
            if (sessionError) {
                return null;
            }
            
            if (session?.access_token) {
                return session.access_token;
            }
        } catch (timeoutError) {
            // Timeout handled
        }

        try {
            const refreshPromise = supabase.auth.refreshSession();
            const refreshTimeout = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('refreshSession timeout')), 3000)
            );
            const { data, error } = await Promise.race([refreshPromise, refreshTimeout]);
            
            if (!error && data.session?.access_token) {
                return data.session.access_token;
            }
        } catch (refreshError) {
            // Refresh failed
        }
        
        return null;
    } catch (error) {
        return null;
    }
}

async function makeAuthenticatedRequest(endpoint, options = {}) {
    const token = await getValidAccessToken();
    if (!token) {
        throw new Error('No access token available. Please log in again.');
    }

    const config = {
        ...options,
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...options.headers
        },
        timeout: options.timeout || 30000
    };

    try {
        const response = await axios({
            url: `${BACKEND_URL}${endpoint}`,
            ...config
        });
        return response.data;
    } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
            const refreshed = await supabase.auth.refreshSession();
            const newToken = refreshed.data?.session?.access_token;
            if (newToken) {
                config.headers['Authorization'] = `Bearer ${newToken}`;
                const retryResponse = await axios({
                    url: `${BACKEND_URL}${endpoint}`,
                    ...config
                });
                return retryResponse.data;
            }
        }
        throw error;
    }
}

// Validate OAuth URL before returning
function validateAuthUrl(authUrl, provider) {
    if (!authUrl || typeof authUrl !== 'string') {
        throw new Error(`Invalid ${provider} authorization URL: URL is missing or invalid`);
    }
    
    if (authUrl.includes('undefined')) {
        throw new Error(
            `Backend configuration error: ${provider} OAuth client ID is not configured. ` +
            `Please check backend environment variables (GOOGLE_CLIENT_ID for YouTube, TWITCH_CLIENT_ID for Twitch, KICK_CLIENT_ID for Kick).`
        );
    }
    
    if (!authUrl.startsWith('http://') && !authUrl.startsWith('https://')) {
        throw new Error(`Invalid ${provider} authorization URL: URL must start with http:// or https://`);
    }
    
    // Check for common OAuth URL issues
    try {
        const url = new URL(authUrl);
        if (!url.searchParams.has('client_id') || url.searchParams.get('client_id') === 'undefined') {
            throw new Error(
                `Backend configuration error: ${provider} OAuth client_id is missing or undefined. ` +
                `The backend must provide a valid OAuth client ID.`
            );
        }
        if (!url.searchParams.has('redirect_uri')) {
            throw new Error(
                `Backend configuration error: ${provider} OAuth redirect_uri is missing. ` +
                `The backend must provide a valid redirect URI.`
            );
        }
    } catch (urlError) {
        if (urlError.message.includes('Backend configuration error')) {
            throw urlError;
        }
        throw new Error(`Invalid ${provider} authorization URL: ${urlError.message}`);
    }
    
    return authUrl;
}

// Get OAuth authorization URL for YouTube
export const getYouTubeAuthUrl = async () => {
    try {
        const response = await makeAuthenticatedRequest('/api/oauth/youtube/authorize', {
            method: 'GET'
        });
        
        // Log the response for debugging (remove in production if needed)
        console.log('YouTube OAuth response:', {
            hasResponse: !!response,
            hasAuthUrl: !!response?.authUrl,
            authUrlPreview: response?.authUrl ? response.authUrl.substring(0, 100) + '...' : 'missing'
        });
        
        if (!response || !response.authUrl) {
            console.error('Backend response missing authUrl:', response);
            throw new Error('Backend did not return a valid authorization URL. Please check backend configuration.');
        }
        
        // Log if we detect undefined in the URL
        if (response.authUrl.includes('undefined')) {
            console.error('Detected undefined in OAuth URL:', {
                fullUrl: response.authUrl,
                clientIdParam: new URL(response.authUrl).searchParams.get('client_id')
            });
        }
        
        return validateAuthUrl(response.authUrl, 'YouTube');
    } catch (error) {
        console.error('Error getting YouTube auth URL:', error);
        
        // Provide more helpful error messages
        if (error.response?.status === 500) {
            throw new Error(
                'Backend server error when generating YouTube OAuth URL. ' +
                'Please ensure the backend has GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET configured.'
            );
        }
        
        if (error.message) {
            throw error;
        }
        
        throw new Error(`Failed to get YouTube authorization URL: ${error.message || 'Unknown error'}`);
    }
};

// Get OAuth authorization URL for Twitch
export const getTwitchAuthUrl = async () => {
    try {
        const response = await makeAuthenticatedRequest('/api/oauth/twitch/authorize', {
            method: 'GET'
        });
        
        // Log the response for debugging (remove in production if needed)
        console.log('Twitch OAuth response:', {
            hasResponse: !!response,
            hasAuthUrl: !!response?.authUrl,
            authUrlPreview: response?.authUrl ? response.authUrl.substring(0, 100) + '...' : 'missing'
        });
        
        if (!response || !response.authUrl) {
            console.error('Backend response missing authUrl:', response);
            throw new Error('Backend did not return a valid authorization URL. Please check backend configuration.');
        }
        
        // Log if we detect undefined in the URL
        if (response.authUrl.includes('undefined')) {
            console.error('Detected undefined in OAuth URL:', {
                fullUrl: response.authUrl,
                clientIdParam: new URL(response.authUrl).searchParams.get('client_id')
            });
        }
        
        return validateAuthUrl(response.authUrl, 'Twitch');
    } catch (error) {
        console.error('Error getting Twitch auth URL:', error);
        
        // Provide more helpful error messages
        if (error.response?.status === 500) {
            throw new Error(
                'Backend server error when generating Twitch OAuth URL. ' +
                'Please ensure the backend has TWITCH_CLIENT_ID and TWITCH_CLIENT_SECRET configured.'
            );
        }
        
        if (error.message) {
            throw error;
        }
        
        throw new Error(`Failed to get Twitch authorization URL: ${error.message || 'Unknown error'}`);
    }
};

// Get OAuth authorization URL for Kick
export const getKickAuthUrl = async () => {
    try {
        const response = await makeAuthenticatedRequest('/api/oauth/kick/authorize', {
            method: 'GET'
        });

        if (!response || !response.authUrl) {
            throw new Error('Backend did not return a valid authorization URL. Please check backend configuration.');
        }

        return validateAuthUrl(response.authUrl, 'Kick');
    } catch (error) {
        console.error('Error getting Kick auth URL:', error);

        if (error.response?.status === 500) {
            throw new Error(
                'Backend server error when generating Kick OAuth URL. ' +
                'Please ensure the backend has KICK_CLIENT_ID and KICK_CLIENT_SECRET configured.'
            );
        }

        if (error.message) {
            throw error;
        }

        throw new Error(`Failed to get Kick authorization URL: ${error.message || 'Unknown error'}`);
    }
};

// Check which accounts are connected
export const getConnections = async () => {
    try {
        const response = await makeAuthenticatedRequest('/api/oauth/connections', {
            method: 'GET'
        });
        return response;
    } catch (error) {
        console.error('Error fetching connections:', error);
        throw error;
    }
};

export const getConnectionStatus = async () => {
    const data = await getConnections();
    const byProvider = Object.fromEntries(
        (data.connections || []).map((c) => [c.provider, c])
    );
    return {
        youtube: !!byProvider.youtube?.connected,
        twitch: !!byProvider.twitch?.connected,
        kick: !!byProvider.kick?.connected,
        youtubeNeedsReauth: !!byProvider.youtube?.needsReauth,
        twitchNeedsReauth: !!byProvider.twitch?.needsReauth,
        kickNeedsReauth: !!byProvider.kick?.needsReauth,
        details: byProvider,
    };
};

// Disconnect an OAuth provider
export const disconnectProvider = async (provider) => {
    try {
        const response = await makeAuthenticatedRequest(`/api/oauth/${provider}/disconnect`, {
            method: 'DELETE'
        });
        return response;
    } catch (error) {
        console.error(`Error disconnecting ${provider}:`, error);
        throw error;
    }
};









